<template>
  <div class="digital-map-project container-page big">
    <h2 class="title title--h2">Карта проектов</h2>
    <div class="digital-map-project__filters">
      <div class="digital-map-project__filters-wrapper">
        <div class="digital-map-project__filters-col">
          <span class="label-field">Страна</span>
          <SelectMultiComponent
            :options="countryList"
            placeholder="Все"
            modifier="rectangular"
            clearClass
            canClear
            v-model="filter.countryIds"
          />
        </div>
        <div class="digital-map-project__filters-col">
          <span class="label-field">Отрасль</span>
          <SelectMultiComponent
            :options="industries"
            placeholder="Все"
            modifier="rectangular"
            clearClass
            canClear
            v-model="filter.industryIds"
          />
        </div>
        <div class="digital-map-project__filters-col">
          <span class="label-field">Цель проекта</span>
          <SelectMultiComponent
            :options="purposes"
            placeholder="Все"
            modifier="rectangular"
            clearClass
            canClear
            v-model="filter.purposeIds"
          />
        </div>
        <div class="digital-map-project__filters-col">
          <span class="label-field">Статус реализации проекта</span>
          <SelectMultiComponent
            :options="statusList"
            placeholder="Все"
            modifier="rectangular"
            clearClass
            canClear
            v-model="filter.statusIds"
          />
        </div>
        <div class="digital-map-project__filters-col">
          <span class="label-field">МПК</span>
          <SelectMultiComponent
            :options="mpk"
            placeholder="Все"
            modifier="rectangular"
            clearClass
            canClear
            v-model="filter.mpkComissionId"
          />
        </div>
        <div class="digital-map-project__filters-col">
          <div class="digital-map-project__filters-buttons">
            <ButtonComponent
              class="w-100"
              mod="gradient-bg"
              :disabled="!loaded || !filterChanged"
              @click="changeFilter"
            >
              Применить
            </ButtonComponent>
            <ButtonComponent class="w-100" :disabled="!loaded || (!hasFilter && !hasAppliedFilter)" @click="resetFilter"
              >Сбросить</ButtonComponent
            >
          </div>
        </div>
      </div>
    </div>

    <Loader
      style="margin: 3rem auto"
      class="d-flex text-center align-items-center justify-content-center project-loader"
      v-if="!loaded"
    />

    <SummaryInfoList class="digital-map-project__summary-info" v-if="projects" :list="summaryInfo" />

    <div class="digital-map-project__nav-row">
      <InvestmentSummary :countryIds="countryIdsForInvestmentSummary" :investmentsSum="investmentsSum" />
      <div class="digital-map-project__nav-buttons" v-if="projects">
        <ButtonComponent mod="border" @click="onProjectStatisticClick"> Аналитика по проектам </ButtonComponent>
        <ButtonComponent mod="border" @click="onProjectsListClick"> Перечень проектов </ButtonComponent>
      </div>
    </div>

    <div class="map-block" v-if="projects">
      <div class="map-block__body">
        <MpkProjectMap
          ref="mapComponent"
          class="map-block__map"
          v-show="loaded"
          :projects="projects.items"
          :maxProjects="projects.maxProjects"
          :selectedCountryId="selectedCountryIdNumber"
          @onCountryHeaderClick="onCountryHeaderClick"
          @onRegionClick="onRegionClick"
          @maxRegionProjectsChanged="onMaxRegionProjectsChanged"
        />
      </div>
      <div class="map-block__footer">
        <MapScaleQuantity
          name="Количество проектов"
          :averageCount="currentAverageProjects"
          :totalCount="currentMaxProjects"
          gradientType="green"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import MapScaleQuantity from '@/common/components/redesigned/MapScaleQuantity.vue';
  import SelectMultiComponent from '@/common/components/SelectMultiComponent.vue';
  import removeEmptyValuesFromObject from '@/common/mixins/removeEmptyValuesFromObject';
  import API_COUNTRY from '@/common/utils';

  import SummaryInfoList from '@/components/lists/SummaryInfoList.vue';
  import Loader from '@/components/Loader.vue';

  import API_NSI from '@/modules/nsi/api/nsi';

  import API from '../api/index';
  import InvestmentSummary from '../components/InvestmentSummary.vue';
  import MpkProjectMap from '../components/mpkProjectMap/MpkProjectMap.vue';
  import { usePageFilters } from '../composables/usePageFilters';
  import LocalConstants from '../constants';
  import { MPK_PROJECT_ACTIONS_NAME, MPK_PROJECT_GETTERS_NAME } from '../store/mpkProject/name';

  const DEFAULT_FILTER = {
    countryIds: null,
    industryIds: null,
    statusIds: null,
    purposeIds: null,
    mpkComissionId: null,
  };

  export default {
    name: 'ProjectsMap',
    setup() {
      const { consumeFilters, saveFilters, navigateWithFilters, updateQueryParams } = usePageFilters(
        LocalConstants.PAGE_KEY.MAP,
      );
      return { consumeFilters, saveFilters, navigateWithFilters, updateQueryParams };
    },
    components: {
      SummaryInfoList,
      InvestmentSummary,
      ButtonComponent,
      SelectMultiComponent,
      Loader,
      MpkProjectMap,
      MapScaleQuantity,
    },
    mixins: [removeEmptyValuesFromObject],
    data() {
      return {
        countries: [],
        purposes: [],
        industries: [],
        statuses: [],
        mpk: [],
        countryIdsForInvestmentSummary: null,
        filter: { ...DEFAULT_FILTER },
        appliedFilter: { ...DEFAULT_FILTER },
        projects: null,
        loaded: false,
        pendingRegionData: null,
        maxRegionProjects: null,
      };
    },
    computed: {
      ...mapGetters({
        investmentsSum: MPK_PROJECT_GETTERS_NAME.getMpkProjectInvestmentsSum,
      }),
      selectedCountryIdNumber() {
        return this.appliedFilter.countryIds ? Number(this.appliedFilter.countryIds) : null;
      },
      countryList() {
        return this.countries.map((i) => ({
          value: i.id,
          label: i.text,
        }));
      },
      statusList() {
        return this.statuses.map((i) => ({
          value: i.id,
          label: i.text,
        }));
      },
      summaryInfo() {
        return [
          { label: 'Всего проектов', value: this.projects?.itemsTotal },
          { label: 'Всего стран', value: this.projects?.countryTotal },
          { label: 'Всего отраслей', value: this.projects?.industryTotal },
          { label: 'Проектов по линии МПК', value: this.projects?.projectsHasMpkTotal },
        ];
      },
      currentMaxProjects() {
        if (this.maxRegionProjects !== null) {
          return this.maxRegionProjects;
        }
        return this.projects?.maxProjects ?? 0;
      },
      currentAverageProjects() {
        const max = this.currentMaxProjects;
        if (max < 40) {
          return Math.floor(max / 3);
        }
        return 20;
      },
      hasFilter() {
        return Object.values(this.filter).some((v) => v !== null && v !== undefined);
      },
      hasAppliedFilter() {
        return Object.values(this.appliedFilter).some((v) => v !== null && v !== undefined);
      },
      filterChanged() {
        return JSON.stringify(this.filter) !== JSON.stringify(this.appliedFilter);
      },
    },
    watch: {
      'filter.countryIds'(newVal) {
        if (this._filterInitializing) return;
        this.loadFilterDependentDictionaries(newVal);
        if (newVal) {
          this.filter.industryIds = null;
          this.filter.purposeIds = null;
          this.filter.statusIds = null;
          this.filter.mpkComissionId = null;
        }
      },
    },
    async created() {
      this._filterInitializing = true;

      const savedFilter = this.consumeFilters();
      const initialFilter = { ...DEFAULT_FILTER, ...(savedFilter ?? {}) };

      for (const key of ['countryIds', 'industryIds', 'statusIds', 'purposeIds']) {
        const val = initialFilter[key];
        if (Array.isArray(val)) initialFilter[key] = val.length === 1 ? val[0] : null;
      }

      API_COUNTRY.loadSelectOptions('countryList', this.countries, true);
      await this.loadFilterDependentDictionaries(initialFilter.countryIds);

      this.filter = { ...initialFilter };
      this.appliedFilter = { ...initialFilter };

      this.$nextTick(() => {
        this._filterInitializing = false;
        this.updateQueryParams({
          countryIds: this.appliedFilter.countryIds != null ? [this.appliedFilter.countryIds] : null,
        });
      });

      this.loadPage();
    },
    methods: {
      ...mapActions({
        getMpkProjectInvestmentsSum: MPK_PROJECT_ACTIONS_NAME.getMpkProjectInvestmentsSum,
      }),
      async loadPage() {
        this.loaded = false;
        this.projects = null;
        this.maxRegionProjects = null;

        const filters = this.objectFormatter({
          countryIds: this.appliedFilter.countryIds ? [this.appliedFilter.countryIds] : null,
          industryIds: this.appliedFilter.industryIds ? [this.appliedFilter.industryIds] : null,
          statusIds: this.appliedFilter.statusIds ? [this.appliedFilter.statusIds] : null,
          purposeIds: this.appliedFilter.purposeIds ? [this.appliedFilter.purposeIds] : null,
          MpkComissionId: this.appliedFilter.mpkComissionId,
          JointProjectMode: LocalConstants.JOINT_PROJECT_MODE.ALL,
        });

        await this.getMpkProjectInvestmentsSum(filters);
        this.countryIdsForInvestmentSummary = this.appliedFilter.countryIds ? [this.appliedFilter.countryIds] : null;

        API.projectsByCountry(filters)
          .then((response) => {
            this.projects = response.data;
          })
          .finally(() => {
            this.loaded = true;

            if (this.pendingRegionData) {
              this.$nextTick(() => {
                this.openRegionModal(this.pendingRegionData);
                this.pendingRegionData = null;
              });
            }
          });
      },
      loadFilterDependentDictionaries(countryId) {
        const request = { pageNumber: 1, pageSize: 100000 };
        const countryParams = countryId ? { countryIds: [countryId] } : {};

        return Promise.all([
          //отрасли
          API_NSI.search('NsiIndustry', { ...request, ...countryParams, isActive: true }).then((response) => {
            if (response.data) {
              this.industries = response.data.items.map((o) => ({ label: o.name, value: o.id }));
            }
          }),
          //цели
          API_NSI.search('MpkProjectPurpose', { ...request, ...countryParams }).then((response) => {
            if (response.data) {
              this.purposes = response.data.items.map((o) => ({ label: o.name, value: o.id }));
            }
          }),
          //статусы
          API_NSI.search('MpkProjectStatus', { ...request, ...countryParams }).then((response) => {
            if (response.data) {
              this.statuses = response.data.items.map((o) => ({ id: o.id, text: o.name }));
            }
          }),
          //МПК
          API.getMpkProject(countryId ? { countryIds: [countryId] } : undefined).then((response) => {
            if (response.data) {
              this.mpk = response.data.map((o) => ({ label: o.name, value: o.id }));
            }
          }),
        ]);
      },
      changeFilter() {
        this.appliedFilter = { ...this.filter };
        const toArr = (val) => (val != null ? [val] : null);
        this.saveFilters({
          ...this.appliedFilter,
          countryIds: toArr(this.appliedFilter.countryIds),
          industryIds: toArr(this.appliedFilter.industryIds),
          statusIds: toArr(this.appliedFilter.statusIds),
          purposeIds: toArr(this.appliedFilter.purposeIds),
        });
        this.loadPage();
      },
      onProjectsListClick() {
        const toArray = (val) => (val != null ? [val] : []);
        this.navigateWithFilters(LocalConstants.PAGE_KEY.LIST, {
          countryIds: toArray(this.appliedFilter.countryIds),
          industryIds: toArray(this.appliedFilter.industryIds),
          statusIds: toArray(this.appliedFilter.statusIds),
          purposeIds: toArray(this.appliedFilter.purposeIds),
          mpkComissionId: this.appliedFilter.mpkComissionId,
        });
        this.$router.push({ name: 'Projects' });
      },
      onProjectStatisticClick() {
        this.navigateWithFilters(LocalConstants.PAGE_KEY.STATISTICS, {
          countryIds: this.appliedFilter.countryIds != null ? [this.appliedFilter.countryIds] : [],
        });
        this.$router.push({ name: 'ProjectsStatistics' });
      },
      onCountryHeaderClick(countryId) {
        if (countryId) {
          this.navigateWithFilters(LocalConstants.PAGE_KEY.LIST, {
            countryIds: [countryId],
            statusIds: this.appliedFilter.statusIds ? [this.appliedFilter.statusIds] : [],
            industryIds: this.appliedFilter.industryIds ? [this.appliedFilter.industryIds] : [],
            purposeIds: this.appliedFilter.purposeIds ? [this.appliedFilter.purposeIds] : [],
            mpkComissionId: this.appliedFilter.mpkComissionId,
          });
          this.$router.push({ name: 'Projects' });
        }
      },
      onRegionClick(data) {
        this.pendingRegionData = data.regionData;

        this.filter = {
          ...this.filter,
          countryIds: data.countryId,
        };

        this.appliedFilter = {
          ...this.appliedFilter,
          countryIds: data.countryId,
        };

        this.loadPage();
      },
      openRegionModal(regionData) {
        if (this.$refs.mapComponent) {
          this.$refs.mapComponent.openRegionModalFromParent(regionData);
        }
      },
      onMaxRegionProjectsChanged(value) {
        this.maxRegionProjects = value;
      },
      resetFilter() {
        this.filter = { ...DEFAULT_FILTER };
        this.appliedFilter = { ...DEFAULT_FILTER };
        this.saveFilters(DEFAULT_FILTER);
        this.loadPage();
      },
    },
  };
</script>

<style src="../styles/digital-map-project.scss" scoped lang="scss" />

<style src="../styles/map-block.scss" scoped lang="scss" />

<style lang="scss">
  @import '@/assets/theme/default/title.scss';
  // fix outline in map
  .digital-map-project {
    .leaflet-interactive {
      outline: none;
    }
  }
</style>
